import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useFormOptions } from 'features/ai-blog-writer/hooks/useFormOptions';
import { Loader, Minus, Plus } from 'lucide-react';
import { tones, types } from 'features/email-generator/constants';
import { emailGenApi } from 'features/email-generator/services/EmailGenApi';
import { useEmailContext } from 'features/email-generator/EmailGeneratorContext';
import { Input } from 'components/ui/input';
import { useState, useEffect } from 'react';

const EmailInputPanel = () => {
  const navigate = useNavigate();
  const {
    updateEmailContent,
    isGenerating,
    setIsGenerating,
    formState,
    setFormState,
  } = useEmailContext();

  const isResultPage = useLocation().pathname === '/email-generator/email';
  const [customType, setCustomType] = useState('');
  const [customTone, setCustomTone] = useState('');
  const [showCustomTypeInput, setShowCustomTypeInput] = useState(false);
  const [showCustomToneInput, setShowCustomToneInput] = useState(false);
  const [draftInputValue, setDraftInputValue] = useState<string>('');

  // Initialize draft input value when formState changes
  useEffect(() => {
    if (formState?.drafts) {
      setDraftInputValue(formState.drafts.toString());
    } else {
      setDraftInputValue('1');
    }
  }, [formState?.drafts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('formState', formState);
    if (formState) {
      const updatedFormState = { ...formState };
      
      if (showCustomTypeInput && customType.trim()) {
        updatedFormState.type = customType.trim();
      }
      
      if (showCustomToneInput && customTone.trim()) {
        updatedFormState.tone = customTone.trim();
      }
      
      if (
        !updatedFormState.type ||
        !updatedFormState.prompt ||
        !updatedFormState.tone ||
        !updatedFormState.language
      ) {
        toast.error('Please fill in all required fields.');
        return;
      }

      try {
        setIsGenerating(true);
        const emailPromises = [];
        for (let i = 0; i < updatedFormState.drafts; i++) {
          emailPromises.push(
            emailGenApi.generateEmail({
              type: updatedFormState.type,
              tone: updatedFormState.tone,
              prompt: updatedFormState.prompt,
              language: updatedFormState.language,
            })
          );
        }

        if (emailPromises.length > 0) {
          let emailResponses: string[] = [];
          try {
            emailResponses = await Promise.all(emailPromises);
          } catch (error) {
            console.error('Error generating emails:', error);
          }

          if (emailResponses) {
            updateEmailContent(emailResponses);
          }
        }

        navigate('/email-generator/email');
      } catch (err: any) {
        console.error('Error generating email:', err);
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const { data } = useFormOptions();

  // Handle draft count changes with validation
  const handleDraftCountChange = (value: string) => {
    setDraftInputValue(value);
    
    const parsedValue = parseInt(value);
    if (!isNaN(parsedValue)) {
      // Ensure value is between 1 and 7
      const validatedValue = Math.min(Math.max(1, parsedValue), 7);
      setFormState({ ...formState, drafts: validatedValue });
    }
  };

  // Handle plus and minus button clicks
  const handleIncrement = () => {
    const currentValue = formState?.drafts || 1;
    if (currentValue < 7) {
      const newValue = currentValue + 1;
      setDraftInputValue(newValue.toString());
      setFormState({ ...formState, drafts: newValue });
    }
  };

  const handleDecrement = () => {
    const currentValue = formState?.drafts || 1;
    if (currentValue > 1) {
      const newValue = currentValue - 1;
      setDraftInputValue(newValue.toString());
      setFormState({ ...formState, drafts: newValue });
    }
  };

  // Handle blur event to validate input when focus leaves
  const handleDraftInputBlur = () => {
    const parsedValue = parseInt(draftInputValue);
    if (isNaN(parsedValue) || parsedValue < 1) {
      setDraftInputValue('1');
      setFormState({ ...formState, drafts: 1 });
    } else if (parsedValue > 7) {
      setDraftInputValue('7');
      setFormState({ ...formState, drafts: 7 });
    }
  };

  if (isResultPage) {
    console.log('formState', formState);

    return (
      <form className="bg-background text-text-primary rounded-lg p-5 w-[23%]">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] leading-4 font-medium">
              Email topic
            </label>
            <textarea
              className="resize-none rounded-[6px] border-background-main bg-background h-20 text-text-primary placeholder:text-text-primary/50 focus:outline-none focus:ring-0 focus:border-primary"
              placeholder="Enter email topic here"
              value={formState?.prompt || ''}
              onChange={(e) =>
                setFormState({ ...formState, prompt: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] leading-4 font-medium">Type</label>
            <Select
              value={formState?.type}
              onValueChange={(value) => {
                if (value === 'custom') {
                  setShowCustomTypeInput(true);
                  setFormState({ ...formState, type: 'custom' });
                } else {
                  setShowCustomTypeInput(false);
                  setFormState({ ...formState, type: value });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {types.map((item: string, index: number) => (
                  <SelectItem key={index} value={item}>
                    {item}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom (enter your own)</SelectItem>
              </SelectContent>
            </Select>
            {showCustomTypeInput && (
              <div className="mt-2">
                <Input
                  placeholder="Enter custom email type"
                  value={customType}
                  onChange={(e) => {
                    setCustomType(e.target.value);
                    setFormState({ ...formState, type: e.target.value });
                  }}
                  className="focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA]"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] leading-4 font-medium">Tone</label>
            <Select
              value={formState?.tone}
              onValueChange={(value) => {
                if (value === 'custom') {
                  setShowCustomToneInput(true);
                  setFormState({ ...formState, tone: 'custom' });
                } else {
                  setShowCustomToneInput(false);
                  setFormState({ ...formState, tone: value });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {tones.map((item: string, index: number) => (
                  <SelectItem key={index} value={item}>
                    {item}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom (enter your own)</SelectItem>
              </SelectContent>
            </Select>
            {showCustomToneInput && (
              <div className="mt-2">
                <Input
                  placeholder="Enter custom tone"
                  value={customTone}
                  onChange={(e) => {
                    setCustomTone(e.target.value);
                    setFormState({ ...formState, tone: e.target.value });
                  }}
                  className="focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA]"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] leading-4 font-medium">
              Language
            </label>
            <Select
              value={formState?.language}
              onValueChange={(value) =>
                setFormState({ ...formState, language: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {data?.languages.map(
                  (item: { name: string }, index: number) => (
                    <SelectItem key={index} value={item?.name}>
                      {item?.name}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] leading-4 font-medium text-text-primary">
              No. of Drafts (1-7)
            </label>
            <div className="flex gap-2">
              <Input
                className="focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA] placeholder:font-normal placeholder:text-[16px] focus:border-[#846CEC]"
                onChange={(e) => handleDraftCountChange(e.target.value)}
                onBlur={handleDraftInputBlur}
                value={draftInputValue}
                min={1}
                max={7}
                type="text"
                inputMode="numeric"
                pattern="[1-7]"
              />
              <Button
                className="h-10 w-10 rounded-md flex items-center justify-center bg-secondary"
                onClick={handleIncrement}
                variant="customSecondary"
                type="button"
                disabled={formState?.drafts >= 7}
              >
                <Plus size={18} />
              </Button>
              <Button
                className="h-10 w-10 rounded-md flex items-center justify-center bg-secondary"
                onClick={handleDecrement}
                variant="customSecondary"
                type="button"
                disabled={formState?.drafts <= 1}
              >
                <Minus size={18} />
              </Button>
            </div>
          </div>
        </div>

        <Button
          className="w-full mt-7 bg-primary text-white"
          variant="customPrimary"
          disabled={isGenerating}
          onClick={handleSubmit}
        >
          {isGenerating ? <Loader className="animate-spin mr-2" /> : null}
          <span>Generate</span>
        </Button>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full h-fit max-w-[600px] ">
      <div className="font-bold">
        <p className="text-xs lg:text-sm font-medium text-text-primary">
          START
        </p>
        <h3 className="text-xl lg:text-2xl text-text-primary">
          Enter Email Details
        </h3>
      </div>
      <form className="bg-background text-text-primary rounded-lg p-6 shadow-sm">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-[14px] leading-4 font-medium ">
              Description
            </label>
            <textarea
              className="bg-background dark:text-white w-full resize-none rounded-lg border border-gray-200 p-3 min-h-[100px]  focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Enter email topic here"
              value={formState?.prompt || ''}
              onChange={(e) =>
                setFormState({ ...formState, prompt: e.target.value })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[14px] leading-4 font-medium ">Type</label>
              <Select
                onValueChange={(value) => {
                  if (value === 'custom') {
                    setShowCustomTypeInput(true);
                    setFormState({ ...formState, type: 'custom' });
                  } else {
                    setShowCustomTypeInput(false);
                    setFormState({ ...formState, type: value });
                  }
                }}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((item: string, index: number) => (
                    <SelectItem key={index} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom (enter your own)</SelectItem>
                </SelectContent>
              </Select>
              {showCustomTypeInput && (
                <div className="mt-2">
                  <Input
                    placeholder="Enter custom email type"
                    value={customType}
                    onChange={(e) => {
                      setCustomType(e.target.value);
                      setFormState({ ...formState, type: e.target.value });
                    }}
                    className="placeholder:text-[#B8BCBA]"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[14px] leading-4 font-medium">Tone</label>
              <Select
                onValueChange={(value) => {
                  if (value === 'custom') {
                    setShowCustomToneInput(true);
                    setFormState({ ...formState, tone: 'custom' });
                  } else {
                    setShowCustomToneInput(false);
                    setFormState({ ...formState, tone: value });
                  }
                }}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {tones.map((item: string, index: number) => (
                    <SelectItem key={index} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom (enter your own)</SelectItem>
                </SelectContent>
              </Select>
              {showCustomToneInput && (
                <div className="mt-2">
                  <Input
                    placeholder="Enter custom tone"
                    value={customTone}
                    onChange={(e) => {
                      setCustomTone(e.target.value);
                      setFormState({ ...formState, tone: e.target.value });
                    }}
                    className="placeholder:text-[#B8BCBA]"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid  grid-cols-1 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[14px] leading-4 font-medium">
                Language
              </label>
              <Select
                onValueChange={(value) => {
                  setFormState({ ...formState, language: value });
                }}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {data?.languages.map(
                    (item: { name: string }, index: number) => (
                      <SelectItem key={index} value={item?.name}>
                        {item?.name}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] leading-4 font-medium">
                No. of Drafts (1-7)
              </label>
              <div className="flex gap-2">
                <Input
                  className="placeholder:font-normal placeholder:text-[16px]"
                  onChange={(e) => handleDraftCountChange(e.target.value)}
                  onBlur={handleDraftInputBlur}
                  value={draftInputValue}
                  min={1}
                  max={7}
                  type="text"
                  inputMode="numeric"
                  pattern="[1-7]"
                />
                <Button
                  className="h-10 w-10 rounded-md flex items-center justify-center bg-secondary"
                  onClick={handleIncrement}
                  variant="customSecondary"
                  type="button"
                  disabled={formState?.drafts >= 7}
                >
                  <Plus size={18} />
                </Button>
                <Button
                  className="h-10 w-10 rounded-md flex items-center justify-center bg-secondary"
                  onClick={handleDecrement}
                  variant="customSecondary"
                  type="button"
                  disabled={formState?.drafts <= 1}
                >
                  <Minus size={18} />
                </Button>
              </div>
            </div>
          </div>

          <Button
            className="w-full bg-primary text-white text-sm hover:bg-primary/90 mt-2"
            disabled={isGenerating}
            onClick={handleSubmit}
          >
            {isGenerating ? <Loader className="animate-spin mr-2" /> : null}
            Generate email
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmailInputPanel;
